/**
 * RateDetailEditButton + RateDetailProvider — Iter 122
 *
 * Shared infrastructure to expose the IronRateDetailModal pattern to
 * any rate-bound row across builders (Restoration Concrete / Iron /
 * Tin Tile / Maintenance / Guttering). Pattern:
 *
 *   1. Wrap the builder root with <RateDetailProvider api={api}>.
 *   2. Inside any descendant, drop <RateDetailEditButton component=...
 *      rate={rateRow} pinContext={...} /> next to an existing
 *      <EditRateLink>.
 *   3. Single shared modal opens at the provider root — no z-index
 *      clashes with nested <details>/dialogs.
 *
 * Design parity with Stage 2 Paint "Edit breakdowns" — same Buy /
 * Material / Labour / Contractor / Hours columns + $/hr back-calc.
 */
import React, { createContext, useCallback, useContext, useState } from 'react';
import IronRateDetailModal from '@/components/IronRateDetailModal';

const RateDetailContext = createContext(null);

export function RateDetailProvider({ api, onSaved, children }) {
  const [state, setState] = useState({
    open: false, component: 'sheet', rate: null, pinContext: null,
  });
  const open = useCallback((component, rate, pinContext) => {
    if (!rate || !rate.id) return;
    setState({ open: true, component, rate, pinContext: pinContext || null });
  }, []);
  const close = useCallback(() => {
    setState(prev => ({ ...prev, open: false }));
  }, []);
  return (
    <RateDetailContext.Provider value={{ open }}>
      {children}
      <IronRateDetailModal
        api={api}
        open={state.open}
        onOpenChange={(v) => v ? null : close()}
        component={state.component}
        rate={state.rate}
        pinContext={state.pinContext}
        onSave={() => { if (onSaved) onSaved(); }}
      />
    </RateDetailContext.Provider>
  );
}

export function useRateDetail() {
  return useContext(RateDetailContext);
}

/**
 * Standalone button — drops next to <EditRateLink> on any rate-bound
 * row. If no provider is mounted above, button silently hides.
 *
 * Props:
 *   component   — IronRateDetailModal preset key (`sheet` | `ridge` |
 *                 `barge` | `valley` | `flashing` | `fastening` | `tile`)
 *   rate        — rate option object (must have `.id`)
 *   pinContext  — optional `{ label, detailOverride, onSaveOverride }`
 *                 — if omitted, modal hides the per-pin override radio
 *                 and only allows Apply-to-master mode.
 *   testid      — data-testid suffix.
 */
export default function RateDetailEditButton({ component, rate, pinContext, testid, label }) {
  const ctx = useRateDetail();
  if (!ctx || !rate || !rate.id) return null;
  const hasOverride = pinContext && pinContext.detailOverride
    && Object.keys(pinContext.detailOverride).length > 0;
  return (
    <button
      type="button"
      data-testid={testid || 'rate-detail-btn'}
      onClick={() => ctx.open(component, rate, pinContext)}
      title="Edit Buy / Material / Labour / Contractor breakdown (same as Stage 2 Paint)"
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded border transition-colors ${
        hasOverride
          ? 'border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100'
          : 'border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
      }`}
    >
      {label || (hasOverride ? '✎ Override' : '✎ Detail')}
    </button>
  );
}
